/**
 * TrustIcons — Hand-crafted SVG icon set for the AnimatedFeatures trust band.
 *
 * Rules:
 *  - 24×24 viewBox, monoline style, strokeWidth 1.8
 *  - stroke="currentColor" — colour is applied via parent CSS
 *  - fill="none" throughout; only terminal points use fill
 *  - No Lucide / Heroicons shapes — these are bespoke
 */

import React from 'react'

type IconProps = {
  className?: string
  size?: number
}

const base = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
}

/* ─────────────────────────────────────────────────────────────────────────────
   ShippingIcon — side-profile delivery van in motion
   A clean rectangular cargo body, stepped cab, two geometric wheels,
   and a subtle speed dash to the left to imply movement.
───────────────────────────────────────────────────────────────────────────── */
export const ShippingIcon: React.FC<IconProps> = ({ className, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    {...base}
  >
    {/* Cargo box */}
    <rect x="1.5" y="5.5" width="11" height="10" rx="1.25" />
    {/* Cab section */}
    <path d="M12.5 8H17L20.5 12V15.5H12.5V8Z" />
    {/* Cab window */}
    <path d="M13.5 9H16.5L18.5 12H13.5V9Z" strokeWidth="1.4" />
    {/* Rear wheel */}
    <circle cx="5.75" cy="16.75" r="1.75" />
    {/* Front wheel */}
    <circle cx="17.25" cy="16.75" r="1.75" />
    {/* Axle cover (visual) */}
    <line x1="1.5" y1="15.5" x2="12.5" y2="15.5" strokeWidth="0.8" strokeOpacity="0.4" />
    {/* Speed lines */}
    <line x1="0" y1="8.5" x2="-1.5" y2="8.5" strokeWidth="1.4" />
    <line x1="0" y1="11" x2="-2" y2="11" strokeWidth="1.4" />
    <line x1="0" y1="13.5" x2="-1.5" y2="13.5" strokeWidth="1.4" />
  </svg>
)

/* ─────────────────────────────────────────────────────────────────────────────
   GuaranteeIcon — certificate seal
   Double-ring seal with a clean checkmark and a small ribbon tab at bottom.
   Outer ring is dashed to echo an official stamp.
───────────────────────────────────────────────────────────────────────────── */
export const GuaranteeIcon: React.FC<IconProps> = ({ className, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    {...base}
  >
    {/* Outer decorative dashed ring */}
    <circle cx="12" cy="11.5" r="9.5" strokeDasharray="2.4 2" strokeWidth="1.1" strokeOpacity="0.55" />
    {/* Inner solid ring */}
    <circle cx="12" cy="11.5" r="7" />
    {/* Checkmark — slightly thick, confident */}
    <polyline points="8.25 11.75 10.75 14.25 15.75 9" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
    {/* Ribbon tails */}
    <path d="M9 19.5L12 21.5L15 19.5" strokeWidth="1.3" />
    <path d="M9 19.5V18M15 19.5V18" strokeWidth="1.3" />
  </svg>
)

/* ─────────────────────────────────────────────────────────────────────────────
   SupportIcon — professional headset
   A simple but refined headset: smooth arc headband, rectangular ear cups,
   a concise mic boom. Deliberately avoids the generic Lucide headphones look.
───────────────────────────────────────────────────────────────────────────── */
export const SupportIcon: React.FC<IconProps> = ({ className, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    {...base}
  >
    {/* Headband arc */}
    <path d="M4.5 12A7.5 7.5 0 0 1 19.5 12" />
    {/* Left ear cup */}
    <rect x="2.25" y="11.5" width="3.25" height="5.5" rx="1.5" />
    {/* Right ear cup */}
    <rect x="18.5" y="11.5" width="3.25" height="5.5" rx="1.5" />
    {/* Neck piece / cord from right ear */}
    <path d="M21.75 15.5A4.5 4.5 0 0 1 17.25 20H15" />
    {/* Mic boom */}
    <path d="M15 20H13.5" />
    {/* Mic capsule dot */}
    <circle cx="13.25" cy="20" r="0.65" fill="currentColor" stroke="none" />
  </svg>
)

/* ─────────────────────────────────────────────────────────────────────────────
   PaymentIcon — secure bank shield
   A shield outline with a minimal bank facade inside (3 columns + pediment).
   Signals security and institutional trust, not credit cards.
───────────────────────────────────────────────────────────────────────────── */
export const PaymentIcon: React.FC<IconProps> = ({ className, size = 22 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={className}
    {...base}
  >
    {/* Shield outline */}
    <path d="M12 2.5L3.5 6.25V12C3.5 16.75 7.2 21.1 12 22.25C16.8 21.1 20.5 16.75 20.5 12V6.25L12 2.5Z" />
    {/* Pediment (triangle top of building) */}
    <path d="M8.5 11L12 8.25L15.5 11" strokeWidth="1.4" />
    {/* Cornice line */}
    <line x1="8" y1="11" x2="16" y2="11" strokeWidth="1.1" />
    {/* Left column */}
    <line x1="9.5" y1="11" x2="9.5" y2="15.75" strokeWidth="1.3" />
    {/* Centre column */}
    <line x1="12" y1="11" x2="12" y2="15.75" strokeWidth="1.3" />
    {/* Right column */}
    <line x1="14.5" y1="11" x2="14.5" y2="15.75" strokeWidth="1.3" />
    {/* Base/steps */}
    <line x1="8" y1="15.75" x2="16" y2="15.75" strokeWidth="1.1" />
    <line x1="7.5" y1="17" x2="16.5" y2="17" strokeWidth="1.3" />
  </svg>
)
